import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Routes } from '@angular/router';
import { MaintenanceRoutine } from '@features/advanced-maintenance/interfaces/maintenance.interface';
import { MaintenanceRoutinesService } from '@features/advanced-maintenance/services/maintenance-routines.service';
import { MaintenanceWorksService } from '@features/advanced-maintenance/services/maintenance-works.service';
import { WorkOrdersService } from '@features/advanced-maintenance/services/work-orders.service';
import { advanceMaintenanceModalityGuard } from '@features/configuration/guards/advance-maintenance-modality.guard';
import { map, Observable, of, switchMap } from 'rxjs';

// class WorkResolver implements Resolve<any> {
//   maintenanceWorkService = inject(MaintenanceWorksService);

//   resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> | Promise<any> | any {
//     return this.maintenanceWorkService.getById(route.params['workId']);
//   }
// }

export const AdvancedMaintenanceRoutes: Routes = [
  {
    path: 'advanced-maintenance',
    title: 'Mantenimiento avanzado',
    canActivate: [advanceMaintenanceModalityGuard],
    data: {
      icon: 'build',
      requiresAdvancedMaintenance: true,
      permission: 'advancedMaintenance.view',
    },
    loadComponent: () => import('@features/advanced-maintenance/advanced-maintenance.component'),
    children: [
      // Ordenes de servicio
      {
        path: 'work-orders',
        title: 'Ordenes de trabajo',
        data: {
          label: 'Ordenes de trabajo',
          icon: 'assignment_add',
          permission: 'workOrders.view',
        },
        loadComponent: () =>
          import('@features/advanced-maintenance/views/work-orders/work-orders.component'),
        children: [
          {
            path: '',
            title: 'Ordenes de trabajo',
            loadComponent: () =>
              import('@features/advanced-maintenance/views/work-orders/overview/overview.component'),
          },
          {
            path: 'detail/:workOrderId',
            title: 'Detalle de la orden',
            loadComponent: () =>
              import('@features/advanced-maintenance/views/work-orders/order-detail/order-detail.component'),
            resolve: {
              workOrder: WorkOrderDetailResolver,
            },
            data: {
              backFallback: '/advanced-maintenance/work-orders',
              containId: true,
            },
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: 'works',
              },
              {
                path: 'works',
                data: {
                  label: 'Trabajos de la OT',
                  icon: 'build',
                  backFallback: '/advanced-maintenance/work-orders',
                },
                loadComponent: () =>
                  import('@features/advanced-maintenance/views/work-orders/order-detail/ot-works/ot-works.component'),
              },
              {
                path: 'spare-parts',
                data: {
                  label: 'Repuestos de la OT',
                  icon: 'build',
                  backFallback: '/advanced-maintenance/work-orders',
                },
                loadComponent: () =>
                  import('@features/advanced-maintenance/views/work-orders/order-detail/ot-spare-parts/ot-spare-parts.component'),
              },
              {
                path: 'total-costs',
                data: {
                  label: 'Costos totales',
                  icon: 'build',
                  backFallback: '/advanced-maintenance/work-orders',
                },
                loadComponent: () =>
                  import('@features/advanced-maintenance/views/work-orders/order-detail/ot-total-costs/ot-total-costs.component'),
              },
              {
                path: 'incidents',
                data: {
                  label: 'Incidentes',
                  icon: 'description',
                  backFallback: '/advanced-maintenance/work-orders',
                },
                resolve: {
                  workOrder: WorkOrderResolver,
                },
                loadComponent: () =>
                  import('@features/reports/views/reports-incident/reports-incident.component'),
              },
              {
                path: 'checklist-alerts',
                data: {
                  label: 'Alertas de chequeo',
                  icon: 'track_changes',
                  backFallback: '/advanced-maintenance/work-orders',
                },
                // loadComponent: () =>
                //   import('@features/advanced-maintenance/views/work-orders/order-detail/ot-checklists-alerts/ot-checklists-alerts.component'),
                resolve: {
                  workOrder: WorkOrderResolver,
                },
                loadComponent: () =>
                  import('@features/reports/views/reports-checklists/report-alert-checklist/report-alert-checklist.component'),
              },
              {
                path: 'maintenance-alerts',
                data: {
                  label: 'Alertas de mantenimiento',
                  icon: 'history',
                  backFallback: '/advanced-maintenance/work-orders',
                },
                // loadComponent: () =>
                //   import('@features/advanced-maintenance/views/work-orders/order-detail/ot-maintenance-alerts/ot-maintenance-alerts.component'),
                resolve: {
                  workOrder: WorkOrderResolver,
                },
                loadComponent: () =>
                  import('@features/advanced-maintenance/views/scheduling-alerts/scheduling-alerts.component'),
              },
            ],
          },
          {
            path: 'order-report/:workOrderId',
            title: 'Reporte de la orden',
            loadComponent: () =>
              import('@features/advanced-maintenance/views/work-orders/order-report/order-report.component'),
            data: {
              backFallback: '/advanced-maintenance/work-orders',
              containId: true,
            },
            resolve: {
              workOrder: WorkOrderResolver,
            },
          },
          {
            path: 'order-tracking/:workOrderId',
            title: 'Seguimiento de la orden',
            loadComponent: () =>
              import('@features/advanced-maintenance/views/work-orders/order-tracking/order-tracking.component'),
            data: {
              backFallback: '/advanced-maintenance/work-orders',
              permission: 'workOrders.manage',
            },
            resolve: {
              workOrder: WorkOrderResolver,
            },
          },
        ],
      },

      // Alerta de programacion
      {
        path: 'scheduling-alerts',
        title: 'Alerta de programacion',
        data: {
          label: 'Alerta de programacion',
          icon: 'warning',
          permission: 'schedulingAlerts.view',
        },
        loadComponent: () =>
          import('@features/advanced-maintenance/views/scheduling-alerts/scheduling-alerts.component'),
      },

      // Repuestos
      {
        path: 'spare-parts',
        title: 'Repuestos',
        data: { label: 'Repuestos', icon: 'build', permission: 'maintenanceCatalogs.view' },
        children: [
          {
            path: '',
            title: 'Repuestos',
            loadComponent: () =>
              import('@features/advanced-maintenance/views/spare-parts/spare-parts.component'),
          },
          {
            path: 'costs-by-supplier',
            title: 'Costos por proveedor',
            data: {
              label: 'Costos por proveedor',
              icon: 'attach_money',
              backFallback: '/advanced-maintenance/spare-parts',
            },
            loadComponent: () =>
              import('@features/advanced-maintenance/views/spare-parts/costs-by-supplier/costs-by-supplier.component'),
          },
        ],
      },

      // Trabajos
      {
        path: 'works',
        title: 'Trabajos',
        data: { label: 'Trabajos', icon: 'construction', permission: 'maintenanceCatalogs.view' },
        children: [
          {
            path: '',
            title: 'Trabajos',
            loadComponent: () =>
              import('@features/advanced-maintenance/views/works/works.component'),
          },
          {
            path: 'periodicity/:workId',
            title: 'Periodicidad de trabajos',
            loadComponent: () =>
              import('@features/advanced-maintenance/views/works/work-periodicity/work-periodicity.component'),
            data: {
              backFallback: '/advanced-maintenance/works', // El always configurado en app.config.ts hace que se herede el backFallback de la ruta padre en los hijos
              containId: true,
            },
            resolve: {
              work: WorkResolver,
            },
            children: [
              {
                path: 'vehicle',
                data: {
                  label: 'Por vehiculo',
                  icon: 'directions_car',
                  backFallback: '/advanced-maintenance/works',
                },
                loadComponent: () =>
                  import('@features/advanced-maintenance/views/works/work-periodicity/periodicity-vehicle/periodicity-vehicle.component'),
              },
              {
                path: 'vehicle-line',
                data: {
                  label: 'Por linea',
                  icon: 'directions_car',
                  backFallback: '/advanced-maintenance/works',
                },
                loadComponent: () =>
                  import('@features/advanced-maintenance/views/works/work-periodicity/periodicity-vehicle-line/periodicity-vehicle-line.component'),
              },
              {
                path: 'vehicle-type',
                data: {
                  label: 'Por tipo',
                  icon: 'directions_car',
                  backFallback: '/advanced-maintenance/works',
                },
                loadComponent: () =>
                  import('@features/advanced-maintenance/views/works/work-periodicity/periodicity-vehicle-type/periodicity-vehicle-type.component'),
              },
              {
                path: 'vehicle-engine',
                data: {
                  label: 'Por motor',
                  icon: 'directions_car',
                  backFallback: '/advanced-maintenance/works',
                },
                loadComponent: () =>
                  import('@features/advanced-maintenance/views/works/work-periodicity/periodicity-vehicle-engine/periodicity-vehicle-engine.component'),
              },
              {
                path: 'vehicle-type-and-engine',
                data: {
                  label: 'Por tipo y motor',
                  icon: 'directions_car',
                  backFallback: '/advanced-maintenance/works',
                },
                loadComponent: () =>
                  import('@features/advanced-maintenance/views/works/work-periodicity/periodicity-vehicle-type-and-engine/periodicity-vehicle-type-and-engine.component'),
              },
              {
                path: '',
                pathMatch: 'full',
                redirectTo: 'vehicle',
              },
            ],
          },
        ],
      },

      // Rutinas de mantenimiento
      //TODO: Habilitar cuando se habilite la funcionalidad de rutinas de mantenimiento
      // {
      //   path: 'maintenance-routines',
      //   title: 'Rutinas de mantenimiento',
      //   data: {
      //     label: 'Rutinas de mantenimiento',
      //     icon: 'route',
      //     permission: 'maintenanceCatalogs.view',
      //   },
      //   children: [
      //     {
      //       path: '',
      //       title: 'Rutinas de mantenimiento',
      //       loadComponent: () =>
      //         import('@features/advanced-maintenance/views/maintenance-routines/maintenance-routines.component'),
      //     },
      //     {
      //       path: 'routine-service',
      //       title: 'Trabajos de la  rutina',
      //       data: {
      //         backFallback: '/advanced-maintenance/maintenance-routines',
      //       },
      //       resolve: {
      //         maintenanceRoutine: MaintenanceRoutineResolver,
      //       },
      //       loadComponent: () =>
      //         import('@features/advanced-maintenance/views/maintenance-routines/routine-services/routine-services.component'),
      //     },
      //     {
      //       path: 'routine-periodicity/:maintenanceRoutineId',
      //       title: 'Periodicidad de la rutina',
      //       data: {
      //         backFallback: '/advanced-maintenance/maintenance-routines',
      //         containId: true,
      //       },
      //       resolve: {
      //         maintenanceRoutine: MaintenanceRoutineResolver,
      //       },
      //       loadComponent: () =>
      //         import('@features/advanced-maintenance/views/maintenance-routines/routine-periodicity/routine-periodicity.component'),
      //       children: [
      //         {
      //           path: 'vehicle',
      //           data: {
      //             label: 'Por vehiculo',
      //             icon: 'directions_car',
      //           },
      //           loadComponent: () =>
      //             import('@features/advanced-maintenance/views/maintenance-routines/routine-periodicity/periodicity-vehicle/periodicity-vehicle.component'),
      //         },
      //         {
      //           path: 'vehicle-line',
      //           data: {
      //             label: 'Por linea',
      //             icon: 'directions_car',
      //           },
      //           loadComponent: () =>
      //             import('@features/advanced-maintenance/views/maintenance-routines/routine-periodicity/periodicity-vehicle-line/periodicity-vehicle-line.component'),
      //         },
      //         {
      //           path: 'vehicle-type',
      //           data: {
      //             label: 'Por tipo',
      //             icon: 'directions_car',
      //           },
      //           loadComponent: () =>
      //             import('@features/advanced-maintenance/views/maintenance-routines/routine-periodicity/periodicity-vehicle-type/periodicity-vehicle-type.component'),
      //         },
      //         {
      //           path: 'vehicle-engine',
      //           data: {
      //             label: 'Por motor',
      //             icon: 'directions_car',
      //           },
      //           loadComponent: () =>
      //             import('@features/advanced-maintenance/views/maintenance-routines/routine-periodicity/periodicity-vehicle-engine/periodicity-vehicle-engine.component'),
      //         },
      //         {
      //           path: 'vehicle-type-and-engine',
      //           data: {
      //             label: 'Por tipo y motor',
      //             icon: 'directions_car',
      //           },
      //           loadComponent: () =>
      //             import('@features/advanced-maintenance/views/maintenance-routines/routine-periodicity/periodicity-vehicle-type-and-engine/periodicity-vehicle-type-and-engine.component'),
      //         },
      //         {
      //           path: '',
      //           pathMatch: 'full',
      //           redirectTo: 'vehicle',
      //         },
      //       ],
      //     },
      //     {
      //       path: 'routine-spare-parts',
      //       title: 'Repuestos de la rutina',
      //       data: {
      //         backFallback: '/advanced-maintenance/maintenance-routines',
      //       },
      //       resolve: {
      //         maintenanceRoutine: MaintenanceRoutineResolver,
      //       },
      //       loadComponent: () =>
      //         import('@features/advanced-maintenance/views/maintenance-routines/routine-spare-parts/routine-spare-parts.component'),
      //     },
      //   ],
      // },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'work-orders',
      },
    ],
  },
];

function WorkResolver(route: ActivatedRouteSnapshot) {
  const workId = route.paramMap.get('workId') ?? '';
  return inject(MaintenanceWorksService).getById(workId);
}

function WorkOrderResolver(route: ActivatedRouteSnapshot) {
  const workOrderId = route.paramMap.get('workOrderId') ?? '';
  return inject(WorkOrdersService).getById(workOrderId);
}

function WorkOrderDetailResolver(route: ActivatedRouteSnapshot) {
  const workOrderId = route.paramMap.get('workOrderId') ?? '';
  const workOrdersService = inject(WorkOrdersService);

  return workOrdersService.getById(workOrderId).pipe(
    switchMap((workOrder) =>
      workOrdersService.refreshTabCounts(workOrder).pipe(map(() => workOrder)),
    ),
  );
}

function MaintenanceRoutineResolver(
  route: ActivatedRouteSnapshot,
): Observable<MaintenanceRoutine | null> {
  const maintenanceRoutineId =
    route.paramMap.get('maintenanceRoutineId') ??
    route.queryParamMap.get('maintenanceRoutineId') ??
    '';

  if (!maintenanceRoutineId) {
    return of(null);
  }

  return inject(MaintenanceRoutinesService).getById(maintenanceRoutineId);
}
